import {
  ValidationMode,
  ValidationResult,
  type ValidatorFn,
  validateRegex,
} from '@funnellab/shared/src/validation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * @deprecated Use `useValidation` from `'@src/hooks/validation/useValidation'` instead
 */
export const useValidation = <T>({
  value,
  validateFn,
  secondaryValidateFn,
  required = false,
  messageKeys = {},
  maxLength,
  regex,
  min,
  max,
  validationMode = ValidationMode.ON_BLUR,
}: {
  value: T;
  validateFn?: ValidatorFn<T>;
  secondaryValidateFn?: ValidatorFn;
  required?: boolean;
  messageKeys?: Partial<
    Record<Exclude<ValidationResult, ValidationResult.VALID>, string>
  >;
  maxLength?: number;
  regex?: string;
  min?: number | string;
  max?: number | string;
  validationMode?: ValidationMode;
}): {
  isValid: boolean;
  isSecondaryValid: boolean;
  errorMessage: string;
  secondaryErrorMessage: string;
  runValidation: (value: T) => void;
  validateSecondaryValue: (secondaryValue: string) => void;
} => {
  const [isValid, setIsValid] = useState(true);
  const [isSecondaryValid, setIsSecondaryValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [secondaryErrorMessage, setSecondaryErrorMessage] = useState('');
  const regexValidateFn = useMemo(() => validateRegex(regex), [regex]);
  const { t } = useTranslation('formValidation');

  const getErrorMessage = useCallback(
    (validationResult: ValidationResult, value: T): string | undefined => {
      switch (validationResult) {
        case ValidationResult.VALID:
          return;
        case ValidationResult.EMPTY:
          // @ts-expect-error dynamic typing for messageKeys does not work
          return t(messageKeys[ValidationResult.EMPTY] ?? 'empty');
        case ValidationResult.TOO_LONG:
          // @ts-expect-error dynamic typing for messageKeys does not work
          return t(messageKeys[ValidationResult.TOO_LONG] ?? 'tooLong', {
            length: typeof value === 'string' ? value.length : 0,
            maxLength,
          });
        case ValidationResult.WRONG_FORMAT:
          // @ts-expect-error dynamic typing for messageKeys does not work
          return t(messageKeys[ValidationResult.WRONG_FORMAT] ?? 'wrongFormat');
        case ValidationResult.OUT_OF_RANGE:
          return t(
            // @ts-expect-error dynamic typing for messageKeys does not work
            messageKeys[ValidationResult.OUT_OF_RANGE] ?? 'outOfRangeMinMax',
            {
              min: min,
              max: max,
            },
          );
        case ValidationResult.OUT_OF_RANGE_MIN:
          return t(
            // @ts-expect-error dynamic typing for messageKeys does not work
            messageKeys[ValidationResult.OUT_OF_RANGE] ?? 'outOfRangeMin',
            {
              min: min,
            },
          );
        case ValidationResult.OUT_OF_RANGE_MAX:
          return t(
            // @ts-expect-error dynamic typing for messageKeys does not work
            messageKeys[ValidationResult.OUT_OF_RANGE] ?? 'outOfRangeMax',
            {
              max: max,
            },
          );
        case ValidationResult.NOT_EQUAL:
          return t(
            // @ts-expect-error dynamic typing for messageKeys does not work
            messageKeys[ValidationResult.NOT_EQUAL] ?? 'notEqual',
          );
        case ValidationResult.DUPLICATE:
          return t(
            // @ts-expect-error dynamic typing for messageKeys does not work
            messageKeys[ValidationResult.DUPLICATE] ?? 'duplicate',
          );
        default:
          return;
      }
    },
    [t, messageKeys, maxLength, min, max],
  );

  const validate = useCallback(
    (value: T) => {
      if (validationMode === ValidationMode.OFF) {
        setIsValid(true);
        return;
      }
      let validationResult = ValidationResult.VALID;
      if (validateFn) {
        validationResult = validateFn(value);
      }
      if (
        typeof value === 'string' &&
        validationResult === ValidationResult.VALID
      ) {
        validationResult = regexValidateFn(value);
      }
      if (!required && validationResult === ValidationResult.EMPTY) {
        setIsValid(true);
      } else {
        const newErrorMessage = getErrorMessage(validationResult, value);
        setErrorMessage(newErrorMessage ?? errorMessage);
        setIsValid(validationResult === ValidationResult.VALID);
      }
    },
    [
      validationMode,
      validateFn,
      required,
      regexValidateFn,
      getErrorMessage,
      errorMessage,
    ],
  );

  const validateSecondaryValue = useCallback(
    (secondaryValue: string) => {
      let validationResult = ValidationResult.VALID;
      if (secondaryValidateFn) {
        validationResult = secondaryValidateFn(secondaryValue);
      }
      // EmptyValue for secondary value is valid
      if (validationResult === ValidationResult.EMPTY) {
        validationResult = ValidationResult.VALID;
      }
      if (validationResult === ValidationResult.VALID) {
        setSecondaryErrorMessage('');
        setIsSecondaryValid(true);
      } else {
        const newErrorMessage = getErrorMessage(validationResult, value);
        setSecondaryErrorMessage(newErrorMessage ?? errorMessage);
        setIsSecondaryValid(false);
      }
    },
    [secondaryValidateFn, value, getErrorMessage, errorMessage],
  );

  useEffect(() => {
    switch (validationMode) {
      case ValidationMode.ALWAYS:
      case ValidationMode.OFF:
        validate(value);
        return;
      case ValidationMode.ON_BLUR:
        return;
    }
  }, [validate, validationMode, value]);

  return {
    isValid,
    isSecondaryValid,
    errorMessage,
    secondaryErrorMessage,
    runValidation: validate,
    validateSecondaryValue,
  };
};

export const formatErrorMessages = (
  isValid: boolean,
  maxLength: number | undefined,
  length: number,
  errorMessage: string,
  additionalErrorMessage?: string | null | false,
): string =>
  [
    isValid &&
      maxLength &&
      length > maxLength * 0.9 &&
      `${length}/${maxLength}`,
    !isValid && errorMessage,
    additionalErrorMessage ? additionalErrorMessage : '',
  ]
    .filter(Boolean)
    .join(' ');
